/**
 * Base Form Schema Interfaces
 * Định nghĩa cấu trúc chính cho hệ thống base form
 */

import { FieldConfig } from './field-config.interface';


/**
 * Main Form Schema Interface
 * Schema chính để config toàn bộ form
 */
export interface FormSchema {
    /** Unique form identifier */
    formId?: string;

    /** Form title (optional) */
    title?: string;

    /** Form description (optional) */
    description?: string;

    /** Array of field configurations */
    fields: FieldConfig[];

    /** Form sections for grouping fields */
    sections?: FormSection[];

    /** Layout configuration */
    layout?: FormLayout;

    /** Form actions (submit, cancel, reset) */
    actions?: FormActions;

    /** Global form options */
    options?: FormOptions;
}

/**
 * Form Section - Group related fields
 */
export interface FormSection {
    /** Section identifier */
    id: string;

    /** Section title */
    title?: string;

    /** Section description */
    description?: string;

    /** Fields in this section (array of field keys) */
    fields: string[];

    /** Section layout */
    layout?: {
        columns?: number;
        collapsible?: boolean;
        collapsed?: boolean;
    };

    /** Conditional display */
    visible?: VisibilityCondition;
}

/**
 * Form Layout Configuration
 */
export interface FormLayout {
    /** Number of columns (1-12) */
    columns?: number;


    /** Gap between fields */
    gap?: string;

    /** Label position */
    labelPosition?: 'top' | 'left' | 'floating';

    /** Form size variant */
    size?: 'sm' | 'md' | 'lg';

    /** Max width of form */
    maxWidth?: string;

    /** CSS class name để tùy chỉnh */
    cssClass?: string;
}

/**
 * Form Actions Configuration
 */
export interface FormActions {
    submit?: ActionButton;
    cancel?: ActionButton;
    reset?: ActionButton;
    custom?: ActionButton[];

    /** Actions position */
    position?: 'left' | 'center' | 'right' | 'space-between';

    /** Sticky actions at bottom */
    sticky?: boolean;
}

export interface ActionButton {
    label: string;
    type?: 'submit' | 'button' | 'reset';
    cssClass?: string;
    icon?: string;
    disabled?: boolean;
    loading?: boolean;
    visible?: boolean;
}

/**
 * Form Options
 */
export interface FormOptions {
    /** Validate on blur */
    validateOnBlur?: boolean;

    /** Validate on change */
    validateOnChange?: boolean;

    /** Show validation on submit only */
    validateOnSubmit?: boolean;

    /** Auto-trim string values */
    autoTrim?: boolean;

    /** Reset form after submit */
    resetOnSubmit?: boolean;

    /** Show asterisk for required fields */
    showRequiredMark?: boolean;

    /** Scroll to first error on submit */
    scrollToError?: boolean;

    /** Debounce time for async validation (ms) */
    asyncValidationDebounce?: number;
}

/**
 * Visibility Condition
 * Điều kiện hiển thị field/section
 */
export interface VisibilityCondition {
    /** Field key to watch */
    field: string;

    /** Operator */
    operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan' | 'isEmpty' | 'isNotEmpty';

    /** Value to compare */
    value?: any;

    /** Combine with AND/OR */
    and?: VisibilityCondition[];
    or?: VisibilityCondition[];
}
